<template>
  <div class="container-l">
    <Header></Header>
    <div class="py-2 bg-gradient-to-b from-red-50 to-transparent w-full h-full lg:pb-2 relative">
      <div class="max-w-md mx-auto p-6">
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 v-if="showLogin" class="text-3xl font-bold text-gray-900 mb-2">
            Welcome back
          </h1>
          <h1 v-else class="text-3xl font-bold text-gray-900 mb-2">
            Welcome aboard
          </h1>
          <p class="text-gray-600">{{ showLogin ? 'Sign in to your account' : 'Create your account' }}</p>
        </div>

        <!-- Auth Card -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <!-- Tab Navigation -->
          <div class="flex rounded-lg bg-gray-100 p-1 mb-6">
            <a
href="/login" :class="[
              'flex-1 text-center py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200',
              showLogin
                ? 'bg-white text-red-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-700'
            ]"
>
              Login
            </a>
            <a
href="/register" :class="[
              'flex-1 text-center py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200',
              !showLogin
                ? 'bg-white text-red-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-700'
            ]"
>
              Register
            </a>
          </div>

          <!-- Login Form -->
          <!-- Google Sign-in Button -->
          <GoogleSignInButton @error="handleGoogleError" />
          <form @submit.prevent v-if="showLogin" class="space-y-6 mt-8">
            <!-- Divider -->
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500">Or continue with email</span>
              </div>
            </div>

            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
v-model="loginForm.username" type="email" id="email"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                placeholder="Enter your email address" required
>
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
v-model="loginForm.password" type="password" id="password"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                placeholder="Enter your password" required
>
            </div>

            <button
@click="signIn" type="submit"
              class="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:ring-4 focus:ring-red-300 focus:outline-none"
>
              Sign In
            </button>

            <div v-if="signInErrorMessage" class="bg-red-50 border border-red-200 rounded-lg p-3">
              <p class="text-red-800 text-sm">
                <span class="font-medium">Error:</span> {{ signInErrorMessage }}
              </p>
            </div>
          </form>

          <!-- Register Form -->
          <form @submit.prevent v-else class="space-y-6 mt-8">
            <!-- Divider -->
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500">Or continue with email</span>
              </div>
            </div>

            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <input
v-model="registerForm.username" type="email" id="email" :disabled="userVerification"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Enter your email address" required
>
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
v-model="registerForm.password" type="password" id="password" :disabled="userVerification"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Create a secure password" required
>
            </div>

            <button
@click="signUp" type="submit" :disabled="userVerification" :class="[
              'w-full font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:ring-4 focus:outline-none',
              userVerification
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-300'
            ]"
>
              Create Account
            </button>

            <div v-if="signUpErrorMessage !== ''" class="bg-red-50 border border-red-200 rounded-lg p-3">
              <p class="text-red-800 text-sm">
                <span class="font-medium">Error:</span> {{ signUpErrorMessage }}
              </p>
            </div>
          </form>

          <!-- Google Error Message -->
          <div v-if="googleErrorMessage" class="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-red-800 text-sm">
              <span class="font-medium">Google Sign-in Error:</span> {{ googleErrorMessage }}
            </p>
          </div>

          <!-- Email Verification Form -->
          <div v-if="userVerification" class="mt-6 pt-6 border-t border-gray-200">
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div class="flex items-center">
                <svg class="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
/>
                </svg>
                <p class="text-blue-800 text-sm font-medium">Check your email for a verification code</p>
              </div>
            </div>

            <form @submit.prevent class="space-y-4">
              <div>
                <label for="userVerificationCode" class="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
v-model="userVerificationCode" type="text" id="userVerificationCode"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-200"
                  placeholder="Enter verification code" required
>
              </div>

              <button
@click="verifyNewUser" type="submit"
                class="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:ring-4 focus:ring-red-300 focus:outline-none"
>
                Verify Account
              </button>

              <div v-if="userVerificationError !== ''" class="bg-red-50 border border-red-200 rounded-lg p-3">
                <p class="text-red-800 text-sm">
                  <span class="font-medium">Error:</span> {{ userVerificationError }}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import Header from "@/components/Header.vue";
import GoogleSignInButton from "@/components/GoogleSignInButton.vue";

import { Auth, Hub } from 'aws-amplify';

import store from "../store";

import { event } from 'vue-gtag'

export default {
  name: "AuthView",
  components: {
    Header,
    GoogleSignInButton
  },

  data() {
    return {
      loginForm: {
        username: '',
        password: '',
      },
      registerForm: {
        username: '',
        password: '',
      },
      newPasswordForm: {
        username: '',
        newpassword1: '',
        newpassword2: ''
      },
      passwordChangeRequired: null,
      userVerification: null,
      userVerificationCode: "",
      userVerificationError: "",
      user: null,
      registerEmail: "",
      signUpErrorMessage: "",
      signInErrorMessage: "",
      googleErrorMessage: "",
      countryName: "Australia"
    }
  },

  computed: {
    isLoggedIn() {
      return this.$store.state.user.loggedIn;
    },
    showLogin() {
      if (this.$route.name === "Login") {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    registerTab() {
      this.showLogin = false
    },
    loginTab() {
      console.log("Login!")
      this.signUpErrorMessage = "";
      this.signInErrorMessage = "";
      this.userVerificationError = "";
      this.showLogin = true
    },
    signIn() {
      const { username, password } = this.loginForm;
      Auth.signIn(username, password)
        .then(user => {
          if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            this.passwordChangeRequired = true;
            this.newPasswordForm.username = username;
            this.user = user;
          } else {
            this.$store.dispatch("fetchUser", user);
            this.$router.push(`/gifts`);
          };
        })
        .catch((error) => {
          this.signInErrorMessage = error.message;
        })
    },

    setNewPassword() {
      const { username, newpassword1, newpassword2 } = this.newPasswordForm;
      // ADD LOGIC TO CONFIRM THE PASSWORDS MATCH
      if (newpassword1 !== newpassword2) {
        this.signUpErrorMessage = "Passwords do not match";
        return;
      }

      Auth.completeNewPassword(
        this.user,
        newpassword1,
      )
        .then((user) => {
          this.$store.dispatch("fetchUser", user);
          $('#authModal').hide();
          $('.modal-backdrop').hide();
        })
        .catch((e) => {
          console.log(e);
        });
    },

    verifyNewUser() {
      const { username, password } = this.registerForm;
      const userVerificationCode = this.userVerificationCode;
      Auth.confirmSignUp(
        username,
        userVerificationCode,
      )
        .then((user) => {
          Auth.signIn(username, password)
            .then(loggedInUser => {
              event('sign_up', { method: 'Google' })
              this.$store.dispatch("fetchUser", loggedInUser);
              this.$router.push(`/gifts`);
            })
        })
        .catch((error) => {
          this.userVerificationError = error.message;
          console.log(error);
        });
    },

    signOut() {
      Auth.signOut()
        .then(user => {
          store.dispatch("fetchUser");
        })
        .catch((error) => console.log(`Error: ${error}`))
    },

    signUp() {
      const { username, password } = this.registerForm;
      Auth.signUp({
        username,
        password,
        attributes: {
          'custom:country': this.countryName,
          'custom:show_mothers_day': 'true',
          'custom:show_fathers_day': 'true',
          'custom:remind_mothers_day': 'true',
          'custom:remind_fathers_day': 'true',
          'custom:remind_christmas': 'true',
          'custom:remind_easter': 'true',
          'custom:remind_valentines': 'true',
        }
      }).then((user) => {
        if (user.userConfirmed === false) {
          this.user = user;
          this.userVerification = true;
        };
        this.signUpErrorMessage = "";
        this.signInErrorMessage = "";
        this.userVerificationError = "";
      }).catch((error) => {
        if (error.code === "UsernameExistsException") {
          this.signUpErrorMessage = "This email is already registered. Please log in.";
        } else {
          this.signUpErrorMessage = error.message;
        }
        console.log(`Error: ${error.message}`);
      });
    },
    expandCountryCode(code) {
      switch (code) {
        case 'AU':
          return 'Australia';
        case 'BR':
          return 'Brazil';
        case 'CA':
          return 'Canada';
        case 'FJ':
          return 'Fiji';
        case 'FR':
          return 'France';
        case 'IN':
          return 'India';
        case 'IE':
          return 'Ireland';
        case 'MX':
          return 'Mexico';
        case 'NL':
          return 'Netherlands';
        case 'NZ':
          return 'New Zealand';
        case 'PH':
          return 'Philippines';
        case 'SC':
          return 'Scotland';
        case 'SG':
          return 'Singapore';
        case 'ZA':
          return 'South Africa';
        case 'GB':
          return 'UK';
        case 'US':
          return 'USA';
        case 'WA':
          return 'Wales';
        default:
          return null;
      }
    },
    handleGoogleError(error) {
      this.googleErrorMessage = error;
      // Clear the error after 5 seconds
      setTimeout(() => {
        this.googleErrorMessage = "";
      }, 5000);
    },
  },
  mounted() {
    Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      if (event === 'autoSignIn') {
        const user = payload.data;
        this.$store.dispatch("fetchUser", user);
        this.$router.push(`/`);
      } else if (event === 'autoSignIn_failure') {
        this.$router.push(`/login`);
      }
    });
  },
  created() {
    if (this.$route.params.email) {
      this.registerEmail = this.$route.params.email;
      this.registerForm.username = this.$route.params.email;
    }

    if (this.$route.query.country) {
      const normalizedQueryParam = this.$route.query.country.trim().toUpperCase();

      if (normalizedQueryParam.length === 2) {
        // Check if the query parameter is a 2-letter country code
        this.countryName = this.expandCountryCode(normalizedQueryParam);
      } else {
        // Check if the query parameter matches any full country name
        this.countryName = this.$route.query.country;
      }
    }

    Auth.currentSession()
      .then(data => console.log("STDOUT: ", data))
      .catch(err => {
        console.log("ERR: ", err)
        this.signOut()
      });
  },

  mounted() {
  }
};
</script>